import React, { useEffect, useRef, Suspense, lazy } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  useColorModeValue,
  Spinner,
  Image as ChakraImage,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { GreshamHeader } from "../components/GreshamHeader";

// Lazy load footer to improve initial page load
const GreshamFooter = lazy(() => import("../components/GreshamFooter").then(module => ({ default: module.GreshamFooter })));

const GreshamCapitalPartners: React.FC = () => {
  const navigate = useNavigate();
  const textColor = useColorModeValue("gray.800", "white");
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if device prefers reduced motion or is a low-performance device
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isLowPerformance = navigator.hardwareConcurrency <= 2 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Disable parallax on low-performance devices
    if (prefersReducedMotion || isLowPerformance) {
      if (parallaxRef.current) {
        parallaxRef.current.style.transform = 'translate3d(0, 0, 0)';
        parallaxRef.current.style.position = 'absolute';
      }
      return;
    }

    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (parallaxRef.current) {
            const scrolled = window.pageYOffset;
            const rate = scrolled * -0.2; // Further reduced parallax intensity
            parallaxRef.current.style.transform = `translate3d(0, ${rate}px, 0)`;
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Optimized image loading for better performance
  useEffect(() => {
    const optimizeImageLoading = () => {
      // Only preload hero image immediately, defer others
      const heroImage = '/background/photo-1513635269975-59663e0ac1ad.jpeg';

      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = heroImage;
      document.head.appendChild(link);

      // Preload focus section images after a delay when user is likely to scroll
      setTimeout(() => {
        const focusImages = [
          '/background/patrick-langwallner-LPv5CAFq0VI-unsplash.jpg',
          '/background/olivie-strauss-6zhPvQSVt3I-unsplash.jpg'
        ];

        focusImages.forEach(src => {
          const img = new Image();
          img.src = src;
        });
      }, 2000); // 2 second delay
    };

    optimizeImageLoading();

    // Performance monitoring for debugging
    if (process.env.NODE_ENV === 'development') {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.entryType === 'measure' && entry.duration > 16.67) {
            console.warn('Performance issue detected:', entry.name, entry.duration + 'ms');
          }
        });
      });
      observer.observe({ entryTypes: ['measure', 'navigation', 'paint'] });
    }
  }, []);

  return (
    <Box
      minHeight="100vh"
      bg="white"
      overflowY="visible"
      overflowX="hidden"
    >
      <GreshamHeader />
      

      {/* Hero Section with Background Image */}
      <Box
        as="section"
        w="full"
        position="relative"
        h="100vh"
        minH="600px"
        overflow="hidden"
        clipPath="inset(0)"
        pt={{ base: "80px", md: "100px" }}
      >
        {/* Background Image Container with Parallax */}
        <Box
          ref={parallaxRef}
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          minWidth="100vw"
          height="120vh"
          backgroundImage="url('/background/photo-1513635269975-59663e0ac1ad.jpeg')"
          backgroundSize="cover"
          backgroundPosition="center center"
          backgroundRepeat="no-repeat"
          filter="brightness(0.7)"
          willChange="transform"
          zIndex={-1}
          css={{
            transform: "translate3d(0, 0, 0)",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden"
          }}
        />
        {/* Text Container - Fixed proportions */}
        <Box
          position="absolute"
          top="20vh"
          left="5vw"
          w="60vw"
          h="50vh"
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          zIndex={100}
          px={{ base: 4, md: 8 }}
        >
          <Box w="100%" h="100%" display="flex" flexDirection="column" justifyContent="center">
            {/* Mercurian Equity Title */}
            <Text
              fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
              color="white"
              fontFamily="'Caslon Classico Regular', serif"
              fontWeight="normal"
              letterSpacing="0.05em"
              className="slide-in-left"
              mb={3}
            >
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </Text>

            {/* Main Heading - Three Lines */}
            <Heading
              as="h1"
              fontSize={{ base: "clamp(1rem, 3.5vw, 6rem)", xl: "6xl" }}
              fontWeight="bold"
              color="white"
              fontFamily="'Carmen Sans Bold', sans-serif"
              lineHeight="1.15"
              w="100%"
              className="slide-in-left-delay-1"
            >
              Guided by market
              <br />
              intelligence, driven by
              <br />
              opportunity.
            </Heading>

            {/* Learn More Button */}
            <Box 
              pt={2}
              className="slide-in-left-delay-2"
            >
              <Box
                as="button"
                display="flex"
                alignItems="center"
                gap={3}
                cursor="pointer"
                transition="all 0.3s ease"
                border="none"
                bg="transparent"
                p={0}
                onClick={() => {
                  navigate("/about");
                  window.scrollTo(0, 0);
                }}
                _hover={{
                  "& .underline-animation": {
                    transform: "scaleX(1)"
                  },
                  "& .circle-button": {
                    bg: "white",
                    borderColor: "white"
                  },
                  "& .circle-button svg": {
                    color: "black"
                  }
                }}
              >
                <Box position="relative" display="inline-block">
                  <Text
                    className="button-text"
                    fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                    fontFamily="'Carmen Sans Medium', sans-serif"
                    fontWeight="500"
                    color="white"
                    letterSpacing="0.02em"
                    position="relative"
                  >
                    Learn More
                  </Text>
                  <Box
                    className="underline-animation"
                    position="absolute"
                    bottom="-2px"
                    left={0}
                    right={0}
                    h="1px"
                    bg="white"
                    transform="scaleX(0)"
                    transformOrigin="left"
                    transition="transform 0.3s ease"
                  />
                </Box>
                <Box
                  className="circle-button"
                  w={{ base: "40px", md: "45px" }}
                  h={{ base: "40px", md: "45px" }}
                  borderRadius="50%"
                  border="1.5px solid white"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  transition="all 0.3s ease"
                  bg="transparent"
                >
                  <Box
                    as="svg"
                    w={{ base: "18px", md: "20px" }}
                    h={{ base: "18px", md: "20px" }}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    color="white"
                    transition="all 0.3s ease"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* About Section - With higher z-index for parallax */}
      <Box
        as="section"
        w="full"
        py={{ base: 16, md: 20 }}
        px={{ base: 8, md: 16, lg: 20 }}
        bg="white"
        mt="-80px"
        position="relative"
        zIndex={20}
        borderRadius="3xl 3xl 0 0"
        boxShadow="0 -20px 40px rgba(0,0,0,0.05)"
      >
        <Box
          px={{ base: 6, md: 12, lg: 16 }}
          w="full"
        >

          <VStack spacing={8} align="center" maxW="900px" mx="auto" textAlign="center">

            {/* Main Heading */}
            <Heading
              as="h2"
              fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
              fontWeight="bold"
              color={textColor}
              fontFamily="'Carmen Sans Bold', sans-serif"
              lineHeight="1.2"
              letterSpacing="0.01em"
            >
              Solutions to help achieve your investing goals
            </Heading>

            {/* Description */}
            <Text
              fontSize={{ base: "sm", md: "md" }}
              color={textColor}
              textAlign="center"
              lineHeight="1.6"
              opacity={0.8}
              maxW="800px"
              fontFamily="'Carmen Sans Light', sans-serif"
              fontWeight="300"
            >
              Mercurian Equity is an investment management firm, focused on delivering investment solutions and strategic capital partnerships for global institutions and sophisticated clients.
            </Text>

            {/* About Us Button */}
            <Box>
              <Box
                as="button"
                display="flex"
                alignItems="center"
                gap={3}
                cursor="pointer"
                transition="all 0.3s ease"
                border="none"
                bg="transparent"
                p={0}
                onClick={() => {
                  navigate("/about");
                  window.scrollTo(0, 0);
                }}
                _hover={{
                  "& .underline-animation": {
                    transform: "scaleX(1)"
                  },
                  "& .circle-button": {
                    bg: "black",
                    borderColor: "black"
                  },
                  "& .circle-button svg": {
                    color: "white"
                  }
                }}
              >
                <Box position="relative" display="inline-block">
                  <Text
                    className="button-text"
                    fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                    fontFamily="'Carmen Sans Medium', sans-serif"
                    fontWeight="500"
                    color="black"
                    letterSpacing="0.02em"
                    position="relative"
                  >
                    About Us
                  </Text>
                  <Box
                    className="underline-animation"
                    position="absolute"
                    bottom="-2px"
                    left={0}
                    right={0}
                    h="1px"
                    bg="black"
                    transform="scaleX(0)"
                    transformOrigin="left"
                    transition="transform 0.3s ease"
                  />
                </Box>
                <Box
                  className="circle-button"
                  w={{ base: "40px", md: "45px" }}
                  h={{ base: "40px", md: "45px" }}
                  borderRadius="50%"
                  border="1.5px solid black"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  transition="all 0.3s ease"
                  bg="white"
                >
                  <Box
                    as="svg"
                    w={{ base: "18px", md: "20px" }}
                    h={{ base: "18px", md: "20px" }}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    color="black"
                    transition="all 0.3s ease"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </Box>
                </Box>
              </Box>
            </Box>
          </VStack>
        </Box>
      </Box>

      {/* Focus Section - With higher z-index for parallax */}
      <Box
        as="section"
        w="full"
        py={{ base: 16, md: 20 }}
        pb={{ base: 0 }}
        mb={{ base: 0 }}
        px={{ base: 8, md: 16, lg: 20 }}
        position="relative"
        zIndex={50}
        bg="white"
        css={{
          contentVisibility: 'auto',
          containIntrinsicSize: '1px 800px'
        }}
      >
        <Box
          bg="#f7f7f7"
          borderRadius="lg"
          py={{ base: 12, md: 16 }}
          pb={{ base: 20, md: 24 }}
          px={{ base: 6, md: 12, lg: 16 }}
          className="has_ae_slider elementor-element elementor-element-8193d97 e-con-full e-flex ae-bg-gallery-type-default e-con e-parent e-lazyloaded"
        >
            {/* Focus Heading */}
            <Box mb={12} textAlign="left">
              <Box
                position="relative"
                display="inline-block"
                cursor="pointer"
                _hover={{
                  "& .underline-animation": {
                    transform: "scaleX(1)"
                  }
                }}
              >
                  <Heading
                    as="h3"
                    className="elementor-heading-title elementor-size-default"
                    fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                    fontWeight="bold"
                    color={textColor}
                    fontFamily="'Carmen Sans Bold', sans-serif"
                    lineHeight="1.2"
                    letterSpacing="0.01em"
                    textAlign="left"
                    textTransform="uppercase"
                  >
                    OUR FOCUS
                  </Heading>
                  <Box
                    className="underline-animation"
                    position="absolute"
                    bottom="-8px"
                    left={0}
                    w="100%"
                    h="1px"
                    bg={textColor}
                    transform="scaleX(0)"
                    transformOrigin="left"
                    transition="transform 0.3s ease"
                  />
                </Box>
            </Box>

            {/* Value Proposition Cards Grid */}
            <SimpleGrid
              columns={{ base: 1, md: 3 }}
              spacing={{ base: 6, md: 4, lg: 6 }}
              className="has_ae_slider elementor-element elementor-element-93070e4 e-con-full e-flex ae-bg-gallery-type-default e-con e-child critical-content"
              w="full"
              justifyItems="stretch"
            >
              {/* Building Financial Security Card */}
              <Box
                className="elementor-element elementor-element-b96b9fb elementor-widget__width-initial elementor-widget-mobile__width-inherit elementor-cta--skin-classic elementor-animated-content elementor-widget elementor-widget-call-to-action performance-card"
                borderRadius="3xl"
                overflow="hidden"
                boxShadow="0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                bg="white"
                h="full"
                display="flex"
                flexDirection="column"
                border="1px solid"
                borderColor="gray.100"
                willChange="transform"
                css={{
                  transition: "transform 0.3s ease-out, box-shadow 0.3s ease-out",
                  "&:hover": {
                    transform: "translate3d(0, -4px, 0)",
                    boxShadow: "0 15px 30px -8px rgba(0, 0, 0, 0.12)"
                  }
                }}
              >
                {/* Top Section with Title */}
                <Box
                  h="180px"
                  position="relative"
                  overflow="hidden"
                >
                  <ChakraImage
                    src="/background/patrick-langwallner-LPv5CAFq0VI-unsplash.jpg"
                    alt="Building financial security"
                    w="full"
                    h="full"
                    objectFit="cover"
                    position="absolute"
                    top={0}
                    left={0}
                    loading="eager"
                    fallback={
                      <Box
                        w="full"
                        h="full"
                        bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      />
                    }
                  />
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    bg="linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0.2))"
                    display="flex"
                    alignItems="flex-end"
                    justifyContent="flex-start"
                    p={{ base: 4, md: 6 }}
                    zIndex={1}
                  >
                    <Heading
                      as="h3"
                      fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
                      fontFamily="'CarmenSans-Heavy', sans-serif"
                      fontWeight="900"
                      color="white"
                      textAlign="left"
                    >
                      Building financial security
                    </Heading>
                  </Box>
                </Box>

                {/* Content Section */}
                <VStack flex="1" p={{ base: 4, md: 6 }} pl={{ base: 2, md: 4 }} spacing={4} align="flex-start" w="full">
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    textAlign="left"
                    lineHeight="1.6"
                    fontFamily="'Carmen Sans Light', sans-serif"
                    fontWeight="300"
                    w="full"
                    alignSelf="flex-start"
                  >
                    Through intelligent capital stewardship, we seek to deliver above-market performance and returns for our investors.
                  </Text>
                </VStack>
              </Box>

              {/* Building Market Leading Businesses Card */}
              <Box
                className="elementor-element elementor-element-20f2221 elementor-widget__width-initial elementor-widget-mobile__width-inherit elementor-cta--skin-classic elementor-animated-content elementor-widget elementor-widget-call-to-action performance-card"
                borderRadius="3xl"
                overflow="hidden"
                boxShadow="0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                bg="white"
                h="full"
                display="flex"
                flexDirection="column"
                border="1px solid"
                borderColor="gray.100"
                willChange="transform"
                css={{
                  transition: "transform 0.3s ease-out, box-shadow 0.3s ease-out",
                  "&:hover": {
                    transform: "translate3d(0, -4px, 0)",
                    boxShadow: "0 15px 30px -8px rgba(0, 0, 0, 0.12)"
                  }
                }}
              >
                {/* Top Section with Title */}
                <Box
                  h="180px"
                  position="relative"
                  backgroundImage="url('/background/adam-borkowski-dH9Bk0I4EGM-unsplash.jpg')"
                  backgroundSize="cover"
                  backgroundPosition="center"
                >
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    bg="linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0.2))"
                    display="flex"
                    alignItems="flex-end"
                    justifyContent="flex-start"
                    p={{ base: 4, md: 6 }}
                  >
                    <Heading
                      as="h3"
                      fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
                      fontFamily="'CarmenSans-Heavy', sans-serif"
                      fontWeight="900"
                      color="white"
                      textAlign="left"
                    >
                      Building market leading businesses
                    </Heading>
                  </Box>
                </Box>

                {/* Content Section */}
                <VStack flex="1" p={{ base: 4, md: 6 }} pl={{ base: 2, md: 4 }} spacing={4} align="flex-start" w="full">
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    textAlign="left"
                    lineHeight="1.6"
                    fontFamily="'Carmen Sans Light', sans-serif"
                    fontWeight="300"
                    w="full"
                    alignSelf="flex-start"
                  >
                    We equip our portfolio companies and their management with an extensive range of tools, systems, processes and capabilities that they need to grow and be leaders in their markets.
                  </Text>
                </VStack>
              </Box>

              {/* Driving Economic Growth Card */}
              <Box
                className="elementor-element elementor-element-1975f8e elementor-widget__width-initial elementor-widget-mobile__width-inherit elementor-cta--skin-classic elementor-animated-content elementor-widget elementor-widget-call-to-action performance-card"
                borderRadius="3xl"
                overflow="hidden"
                boxShadow="0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                bg="white"
                h="full"
                display="flex"
                flexDirection="column"
                border="1px solid"
                borderColor="gray.100"
                transform="translate3d(0, 0, 0)"
                _hover={{
                  transform: "translate3d(0, -8px, 0)",
                  boxShadow: "0 20px 40px -10px rgba(0, 0, 0, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.08)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                }}
              >
                {/* Top Section with Title */}
                <Box
                  h="180px"
                  position="relative"
                  backgroundImage="url('/background/olivie-strauss-6zhPvQSVt3I-unsplash.jpg')"
                  backgroundSize="cover"
                  backgroundPosition="center"
                >
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    bg="linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0.2))"
                    display="flex"
                    alignItems="flex-end"
                    justifyContent="flex-start"
                    p={{ base: 4, md: 6 }}
                  >
                    <Heading
                      as="h3"
                      fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
                      fontFamily="'CarmenSans-Heavy', sans-serif"
                      fontWeight="900"
                      color="white"
                      textAlign="left"
                    >
                      Driving economic growth
                    </Heading>
                  </Box>
                </Box>

                {/* Content Section */}
                <VStack flex="1" p={{ base: 4, md: 6 }} pl={{ base: 2, md: 4 }} spacing={4} align="flex-start" w="full">
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    textAlign="left"
                    lineHeight="1.6"
                    fontFamily="'Carmen Sans Light', sans-serif"
                    fontWeight="300"
                    w="full"
                    alignSelf="flex-start"
                  >
                    We contribute to the growth of both domestic and international markets through direct investment and value creation.
                  </Text>
                </VStack>
              </Box>
            </SimpleGrid>
        </Box>
      </Box>

      <Box position="relative" zIndex={50} mt={0}>
        <Suspense fallback={
          <Box display="flex" justifyContent="center" alignItems="center" h="200px">
            <Spinner size="lg" color="gray.500" />
          </Box>
        }>
          <GreshamFooter />
        </Suspense>
      </Box>
    </Box>
  );
};

export default GreshamCapitalPartners;